import * as SwitchPrimitives from '@radix-ui/react-switch'
import clsx from 'clsx'
import * as React from 'react'
import { useEffect, useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import ChevronDown from '@/components-latest/primitives/Icons/ChevronDown'
import Spinner from '@/components-latest/primitives/Spinner'
import Text from '@/components-latest/primitives/Text'
import UpdateStoreStatus from '@/components-latest/shared/Modals/UpdateStoreStatus'
import AutoAcceptInfoModal from '@/components-latest/ui/AccountPage/AutoAcceptInfoModal/AutoAcceptInfoModal'
import { useToaster } from '@/components-latest/ui/Toaster/useToaster/useToaster'
import { STORE_AVAILABILITY, BUSY_STATUS } from '@/constants/common'
import {
  useGetStoreAutoAcceptStatusQuery,
  useGetStoreBusyStatusQuery,
  useUpdateStoreAutoAcceptStatusMutation,
  useUpdateStoreBusyStatusMutation
} from '@/ducks/store/api/store.api'

import Styles from './SwitchWithWordsWithModal.module.scss'
import { useSelector } from 'react-redux'
import { selectIsPostRejection } from '@/ducks/store/selectors/store.selectors'
import AutomaticOnlineRestriction from '@/components-latest/ui/OnlineRestriction/AutomaticOnlineRestriction/AutomaticOnlineRestriction'

type StatusType = 0 | 1 | 2

interface SwitchWithWordsWithModalProps {
  onToggle: (status?: StatusType) => void
  status: StatusType
  isLoading?: boolean
  busyTimer?: string
  disabled?: boolean
  className?: string
  width?: number
  storeId?: string
}

interface StatusConfig {
  text: string
  dotClass: string
  containerClass: string
}

const SwitchWithWordsWithModal = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitives.Root>,
  SwitchWithWordsWithModalProps &
    React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>
>(
  (
    {
      className,
      onToggle,
      disabled,
      isLoading,
      status,
      busyTimer,
      width = 180,
      storeId,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation()
    const { addToast } = useToaster()

    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isOpenAutoModal, setIsOpenAutoModal] = useState(false)
    const [isBusyDeactivationInfoOpen, setIsBusyDeactivationInfoOpen] = useState(false)
    const [remainingTime, setRemainingTime] = useState<string>('')
    const [endTime, setEndTime] = useState<number | null>(null)
    const [isAutomaticExpiration, setIsAutomaticExpiration] = useState(false)
    const [userInitiatedChange, setUserInitiatedChange] = useState(false)

    const { data: busyStatusData, refetch: refetchBusyStatus } =
      useGetStoreBusyStatusQuery(undefined, {
        pollingInterval: 30000
      })
    const { data: autoAcceptStatusData } = useGetStoreAutoAcceptStatusQuery()

    const [updateBusyStatus] = useUpdateStoreBusyStatusMutation()
    const [updateAutoAcceptStatus] = useUpdateStoreAutoAcceptStatusMutation()

    // Computed values
    const isOffline = status === STORE_AVAILABILITY.OFFLINE
    const isBusyFromAPI = busyStatusData?.isBusy
    const isBusyDisabled = isOffline
    const isPostRejection = useSelector(selectIsPostRejection)
    useEffect(() => {
      if (busyStatusData?.isBusy && busyStatusData?.busyUntil) {
        setEndTime(busyStatusData.busyUntil)
      } else {
        setEndTime(null)
        setRemainingTime('')
      }
    }, [busyStatusData?.isBusy, busyStatusData?.busyUntil])

    useEffect(() => {
      if (!endTime) {
        setRemainingTime('')
        return
      }

      const updateTimer = () => {
        const now = new Date().getTime()
        const timeLeft = endTime - now

        if (timeLeft > 0) {
          const minutes = Math.floor(timeLeft / (1000 * 60))
          const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000)
          setRemainingTime(`${minutes}:${seconds.toString().padStart(2, '0')}`)
        } else {
          setRemainingTime('')
          setEndTime(null)
          setIsAutomaticExpiration(true)
          updateBusyStatus({
            status: BUSY_STATUS.NON_BUSY
          })
            .then(() => {
              addToast({
                type: 'system',
                message: t('store-notifications.back-online'),
                intent: 'success',
                duration: 5000
              })
              setTimeout(() => setIsAutomaticExpiration(false), 100)
            })
            .catch(error => {
              console.error('Failed to turn off busy mode:', error)
              setIsAutomaticExpiration(false)
            })
        }
      }
      updateTimer()

      const interval = setInterval(updateTimer, 1000)

      return () => clearInterval(interval)
    }, [endTime, updateBusyStatus, addToast, t])

    // Status configuration
    const getStatusConfig = useCallback(
      (status: StatusType): StatusConfig => {
        switch (status) {
          case STORE_AVAILABILITY.OFFLINE:
            return {
              text: t('switch-with-words.statuses.offline'),
              dotClass: Styles.statusDotInactive,
              containerClass: Styles.statusOffline
            }
          case BUSY_STATUS.NON_BUSY:
            return {
              text: t('switch-with-words.statuses.accepting'),
              dotClass: Styles.statusDotActive,
              containerClass: Styles.statusAccepting
            }
          case BUSY_STATUS.BUSY:
            return {
              text: `${t('switch-with-words.statuses.busy')} ${
                isBusyFromAPI && remainingTime ? remainingTime : ''
              }`,
              dotClass: Styles.statusDotBusy,
              containerClass: Styles.statusBusy
            }
          default:
            return {
              text: t('switch-with-words.statuses.accepting'),
              dotClass: Styles.statusDotActive,
              containerClass: Styles.statusAccepting
            }
        }
      },
      [t, isBusyFromAPI, remainingTime]
    )

    const handleClick = useCallback(() => {
      if (isPostRejection) {
        setIsOpenAutoModal(true)
        return
      }
      setUserInitiatedChange(true)
      setIsModalOpen(true)
    }, [isPostRejection])

    const handleStatusUpdate = useCallback(
      async (newStatus: StatusType) => {
        // Only allow status changes when the user explicitly initiated the change
        if (!userInitiatedChange) {
          return
        }

        if (
          status === STORE_AVAILABILITY.OFFLINE &&
          newStatus === BUSY_STATUS.NON_BUSY
        ) {
          // Offline → Accepting orders
          onToggle(STORE_AVAILABILITY.ONLINE)
        } else if (
          status === BUSY_STATUS.NON_BUSY &&
          newStatus === BUSY_STATUS.BUSY
        ) {
          onToggle(newStatus)

          if (autoAcceptStatusData?.store_auto_accept_orders_store_enabled) {
            try {
              await updateAutoAcceptStatus({
                store_auto_accept_orders_store_enabled: false
              }).unwrap()
            } catch (error) {
              console.error(
                'Failed to disable auto-accept when entering busy mode:',
                error
              )
            }
          }

          setIsBusyDeactivationInfoOpen(true)
        } else if (
          status === BUSY_STATUS.BUSY &&
          newStatus === BUSY_STATUS.NON_BUSY
        ) {
          // Busy → Accepting orders
          onToggle(newStatus)
          if (!isAutomaticExpiration) {
            addToast({
              type: 'system',
              message: t('store-notifications.back-online'),
              intent: 'success',
              duration: 5000
            })
          }
        } else {
          onToggle(newStatus)
        }

        if (
          newStatus === BUSY_STATUS.BUSY ||
          newStatus === BUSY_STATUS.NON_BUSY
        ) {
          try {
            await refetchBusyStatus()
          } catch (error) {
            console.error('Failed to refetch busy status:', error)
          }
        }

        // Reset the user initiated flag after the status update
        setUserInitiatedChange(false)
      },
      [
        status,
        onToggle,
        refetchBusyStatus,
        updateAutoAcceptStatus,
        autoAcceptStatusData?.store_auto_accept_orders_store_enabled,
        isAutomaticExpiration,
        addToast,
        t,
        userInitiatedChange
      ]
    )

    // Memoized values
    const statusConfig = useMemo(
      () => getStatusConfig(status),
      [getStatusConfig, status]
    )

    // Render functions
    const renderStatusIndicator = () => {
      if (status === BUSY_STATUS.BUSY && isBusyFromAPI) {
        return (
          <div className={Styles.pauseIcon}>
            <div className={Styles.pauseBar} />
            <div className={Styles.pauseBar} />
          </div>
        )
      }

      return <div className={clsx(Styles.statusDot, statusConfig.dotClass)} />
    }

    return (
      <>
        <SwitchPrimitives.Root
          className={clsx(Styles.root, statusConfig.containerClass, className)}
          data-status={status}
          data-testid="switch-with-modal"
          disabled={disabled || isLoading}
          onClick={handleClick}
          style={
            {
              '--block-width': typeof width === 'number' ? `${width}px` : width
            } as React.CSSProperties
          }
          {...props}
          ref={ref}>
          <div className={Styles.statusIndicator}>
            {renderStatusIndicator()}
          </div>
          <div className={Styles.content}>
            <Text weight="bold" className={Styles.word} asChild>
              <p>{statusConfig.text}</p>
            </Text>
          </div>
          <div className={Styles.chevronContainer}>
            <ChevronDown className={Styles.chevron} />
          </div>
          {isLoading && (
            <div className={Styles.loadingOverlay}>
              <Spinner />
            </div>
          )}
        </SwitchPrimitives.Root>

        <UpdateStoreStatus
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false)
            setUserInitiatedChange(false)
          }}
          currentStatus={status}
          onStatusUpdate={handleStatusUpdate}
          isLoading={isLoading}
          showBusyConfirmation={status === BUSY_STATUS.BUSY && isBusyFromAPI}
          storeId={storeId}
        />
        <AutomaticOnlineRestriction
          isOpen={isOpenAutoModal}
          onClose={() => {
            setIsOpenAutoModal(false)
          }}
        />
        <AutoAcceptInfoModal
          open={isBusyDeactivationInfoOpen}
          onOpenChange={setIsBusyDeactivationInfoOpen}
          title={t('busy-mode-auto-accept-info.title')}
          description={t('busy-mode-auto-accept-info.description')}
          confirmLabel={t('busy-mode-auto-accept-info.confirm')}
        />
      </>
    )
  }
)
SwitchWithWordsWithModal.displayName = 'SwitchWithWordsWithModal'

export default SwitchWithWordsWithModal
